/**
 * Google Material Symbols, Apache-2.0 (google/material-design-icons), ported from
 * `prototype/assets/icons.js`. These are *filled* paths on a `0 -960 960 960` grid, so they
 * cannot go through `Icon.tsx`'s stroked renderer — that would draw them as an outline of the
 * silhouette. Only Gate Detail's Pedestrian command uses this family, for `directions-walk`.
 */

import { UI_SCALE } from '../../theme/tokens'

const MATERIAL_PATHS = {
  'directions-walk':
    'm280-40 112-564-72 28v136h-80v-188l202-86q14-6 29.5-7t29.5 4q14 5 26.5 14t20.5 23l40 64q26 42 70.5 69T760-520v80q-70 0-125-29t-94-74l-25 123 84 80v300h-80v-260l-84-64-72 324h-84Zm260-700q-33 0-56.5-23.5T460-820q0-33 23.5-56.5T540-900q33 0 56.5 23.5T620-820q0 33-23.5 56.5T540-740Z',
} as const

export type MaterialIconName = keyof typeof MATERIAL_PATHS

export function MaterialIcon({
  name,
  size = 16,
  className = '',
}: {
  readonly name: MaterialIconName
  readonly size?: number
  readonly className?: string
}): JSX.Element {
  // Presentation attributes, not `style` — see `Icon.tsx`'s comment: a `style` prop beats every
  // class-selector override in `global.css` regardless of specificity. The scale is multiplied
  // here for the reason spelled out there too: `var()` in an SVG length presentation attribute
  // is a Chrome 139 feature, and an engine without it drops the attribute rather than defaulting
  // it, leaving a `viewBox`-only `<svg>` to fill its container.
  const scaled = `${size * UI_SCALE}px`
  return (
    <svg
      className={`material material-${name} ${className}`.trim()}
      width={scaled}
      height={scaled}
      viewBox="0 -960 960 960"
      fill="currentColor"
      stroke="none"
      aria-hidden="true"
    >
      <path d={MATERIAL_PATHS[name]} />
    </svg>
  )
}
